import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, RevealImage } from "@/lib/motion";
import { Page, PageHero, Overline, CTABand } from "@/components/shared";
import { useContent } from "@/lib/ContentProvider";

export default function Blog() {
  const { posts } = useContent();
  const [feature, ...rest] = posts;
  if (!feature) {
    return (
      <Page testid="blog-page">
        <PageHero overline="Blog" titleLines={["Coming soon"]} />
        <CTABand />
      </Page>
    );
  }
  return (
    <Page testid="blog-page">
      <PageHero
        overline="Blog & Insights"
        titleLines={["Notes on modern", <span key="2">surgical <span className="italic text-alch-terracotta">care</span></span>]}
        subtitle="Clear, patient-friendly guides on laser and laparoscopic treatments from the ALCH team."
      />

      <section className="px-5 py-24 md:px-8 md:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Link to={`/blog/${feature.slug}`} data-testid="blog-feature" className="group grid overflow-hidden rounded-[2rem] border border-alch-forest/10 bg-alch-surface lg:grid-cols-2">
              <RevealImage src={feature.image} alt={feature.title} className="h-72 lg:h-full" imgClassName="transition-transform duration-700 group-hover:scale-105" />
              <div className="flex flex-col justify-center p-8 md:p-12">
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.16em] text-alch-terracotta">
                  <span>{feature.tag}</span><span className="text-alch-muted">· {feature.date}</span>
                </div>
                <h2 className="mt-5 font-serif text-3xl leading-tight text-alch-forest md:text-4xl">{feature.title}</h2>
                <p className="mt-4 leading-relaxed text-alch-ink/75">{feature.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-alch-forest">
                  Read article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>

          <div className="mt-16">
            <Overline>More articles</Overline>
            <div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((b, i) => (
                <Reveal key={b.slug} delay={i * 0.08}>
                  <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group block">
                    <div className="overflow-hidden rounded-[1.5rem]">
                      <RevealImage src={b.image} alt={b.title} className="h-56" imgClassName="transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-alch-terracotta">
                      <span>{b.tag}</span><span className="text-alch-muted">· {b.read}</span>
                    </div>
                    <h3 className="mt-3 font-serif text-2xl leading-snug text-alch-forest">{b.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-alch-ink/70">{b.excerpt}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </Page>
  );
}
